import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, Loader2, RefreshCw, Timer } from 'lucide-react';
import { useApi } from '../../hooks/useApi';

/**
 * Pipeline Velocity Tile (May 9, 2026)
 *
 * Read-only Dashboard KPI tile showing weekly stage-progression rate and
 * average days spent per pipeline stage. Pure consumer of
 * `GET /api/pipeline/velocity?window=...` — no schema awareness.
 */

const WINDOW_OPTIONS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
];

function avgTone(days) {
  // Heuristic thresholds: < 7d = healthy, 7-21d = watch, > 21d = stalled
  if (days <= 7) {
    return { ring: 'border-emerald-200 bg-emerald-50 text-emerald-800', dot: 'bg-emerald-500' };
  }
  if (days <= 21) {
    return { ring: 'border-amber-200 bg-amber-50 text-amber-800', dot: 'bg-amber-500' };
  }
  return { ring: 'border-red-200 bg-red-50 text-red-800', dot: 'bg-red-500' };
}

function StageAvgPill({ entry, testid }) {
  const tone = avgTone(entry.avg_days);
  return (
    <div className={`rounded-sm border px-3 py-2 ${tone.ring}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80 truncate">
          {entry.stage_name}
        </p>
        <span
          className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${tone.dot}`}
          aria-hidden="true"
        />
      </div>
      <div className="flex items-baseline gap-1.5 mt-0.5">
        <span className="text-xl font-semibold tabular-nums" data-testid={`${testid}-days`}>
          {entry.avg_days}
        </span>
        <span className="text-[11px] font-normal opacity-70">d avg</span>
        <span className="text-[10px] opacity-50 ml-auto" title={`${entry.sample_size} samples`}>
          n={entry.sample_size}
        </span>
      </div>
    </div>
  );
}

export default function PipelineVelocityTile() {
  const api = useApi();
  const [window, setWindow] = useState('7d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVelocity = useCallback(
    async (w) => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/pipeline/velocity?window=${w}`);
        setData(res);
      } catch (err) {
        setError(err?.message || 'Failed to load velocity');
      } finally {
        setLoading(false);
      }
    },
    [api]
  );

  useEffect(() => {
    fetchVelocity(window);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const dealsAdvanced = data?.deals_advanced ?? 0;
  const totalAdvances = data?.total_advances ?? 0;
  const stages = data?.avg_days_per_stage ?? [];
  const empty = !loading && !error && dealsAdvanced === 0 && stages.length === 0;

  return (
    <Card
      className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:border-emerald-300/60 hover:shadow-md transition-all duration-300"
      data-testid="pipeline-velocity-tile"
    >
      <CardContent className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-sm bg-emerald-50 text-emerald-700 flex-shrink-0">
              <TrendingUp className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                Pipeline
              </p>
              <h3 className="text-sm font-semibold text-stone-900 truncate">
                Velocity · last {window}
              </h3>
            </div>
          </div>
          <div
            className="flex items-center rounded-sm border border-stone-200 bg-stone-50 p-0.5"
            data-testid="velocity-window-switcher"
          >
            {WINDOW_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setWindow(opt.id)}
                className={`px-2 py-0.5 text-[11px] font-semibold rounded-sm transition-colors ${
                  window === opt.id
                    ? 'bg-white text-stone-900 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
                data-testid={`velocity-window-${opt.id}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-6 text-stone-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-xs">Loading…</span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-4">
            <p className="text-xs text-red-700 mb-2">{error}</p>
            <button
              type="button"
              onClick={() => fetchVelocity(window)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-stone-600 hover:text-stone-900"
              data-testid="velocity-retry"
            >
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && empty && (
          <div className="py-5 text-center text-xs text-stone-500" data-testid="velocity-empty">
            No pipeline activity in the last {window}.
            <p className="mt-1 text-[11px] text-stone-400">
              Move deals between stages to start tracking velocity.
            </p>
          </div>
        )}

        {/* Body */}
        {!loading && !error && !empty && (
          <>
            {/* Big number */}
            <div className="flex items-baseline gap-2 mb-3">
              <span
                className="text-3xl font-light text-stone-900 tabular-nums"
                data-testid="velocity-deals-advanced"
              >
                {dealsAdvanced > 0 ? `+${dealsAdvanced}` : '0'}
              </span>
              <span className="text-xs text-stone-500">
                deal{dealsAdvanced === 1 ? '' : 's'} advanced
                {totalAdvances > dealsAdvanced && (
                  <span className="text-stone-400" data-testid="velocity-total-advances">
                    {' '}({totalAdvances} stage moves)
                  </span>
                )}
              </span>
              {data?.scope === 'workspace' && (
                <Badge
                  variant="outline"
                  className="ml-auto text-[9px] uppercase tracking-wider font-semibold border-stone-300 text-stone-600"
                >
                  workspace
                </Badge>
              )}
            </div>

            {/* Per-stage avg days */}
            {stages.length > 0 ? (
              <>
                <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider font-semibold text-stone-500">
                  <Timer className="h-3 w-3" />
                  <span>Avg time in stage</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {stages.slice(0, 4).map((entry) => (
                    <StageAvgPill
                      key={entry.stage}
                      entry={entry}
                      testid={`velocity-stage-${entry.stage}`}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p className="text-[11px] text-stone-500 italic">
                Not enough stage history yet to compute averages.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
